import { formatDateTime } from "../../formatting/date";
import { formatCount, isValidCount } from "../../formatting/number";

/**
 * campaign-pace-metrics: pure derivations for the campaign detail's "Tiempo de envío" and
 * "Ritmo promedio" cards. No I/O, no React, never reads the real clock (callers pass `nowMs`).
 *
 * Single source of truth: the API only ships the first/last instants an email was sent
 * (`primerEnvioEn`/`ultimoEnvioEn`) plus `enviados`; duration and pace are derived here.
 * Copy is intentionally non-technical (no provider/ledger/queue jargon).
 */

const SECOND_MS = 1000;
const MINUTE_MS = 60 * SECOND_MS;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

export type PaceUnavailableReason = "unknown" | "single";

export interface SendPace {
  /** True while the campaign is still sending (elapsed counts up to "now"). */
  inProgress: boolean;
  /** Elapsed sending time in ms, or `null` when the data is missing/inconsistent. */
  elapsedMs: number | null;
  /** Average emails per hour, or `null` when it would be misleading/unknown. */
  perHour: number | null;
  /** Why `perHour` is null (undefined when it is not). */
  paceReason?: PaceUnavailableReason;
}

export interface SendPaceInput {
  estado: string;
  enviados: number | null | undefined;
  primerEnvioEn: string | null | undefined;
  ultimoEnvioEn: string | null | undefined;
  nowMs: number;
}

function parseInstant(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? null : ms;
}

export function computeSendPace(input: SendPaceInput): SendPace {
  const inProgress = input.estado === "enviando";
  const first = parseInstant(input.primerEnvioEn);
  const last = parseInstant(input.ultimoEnvioEn);

  let end: number | null;
  if (first === null) {
    end = null;
  } else if (inProgress) {
    end = Number.isFinite(input.nowMs) ? input.nowMs : last;
  } else {
    end = last;
  }

  if (first === null || end === null || (!inProgress && end < first)) {
    return { inProgress, elapsedMs: null, perHour: null, paceReason: "unknown" };
  }

  const elapsedMs = Math.max(end - first, 0);
  const sent = isValidCount(input.enviados) ? input.enviados : null;
  if (sent === null) return { inProgress, elapsedMs, perHour: null, paceReason: "unknown" };
  // Relaxed (pace-card-live-update): any send with 2+ emails and a positive duration has a pace,
  // however short (3 emails in 80 s is about 135 per hour). Zero duration cannot be divided.
  if (sent < 2 || elapsedMs <= 0) return { inProgress, elapsedMs, perHour: null, paceReason: "single" };
  return { inProgress, elapsedMs, perHour: sent / (elapsedMs / HOUR_MS) };
}

/** "45 s", "12 min", "1 h 25 min", "2 d 3 h" — the two most significant units, floored. */
export function formatSendDuration(ms: number): string {
  if (ms < SECOND_MS) return "menos de 1 s";
  if (ms < MINUTE_MS) return `${Math.floor(ms / SECOND_MS)} s`;
  if (ms < HOUR_MS) return `${Math.floor(ms / MINUTE_MS)} min`;
  if (ms < DAY_MS) {
    const hours = Math.floor(ms / HOUR_MS);
    const minutes = Math.floor((ms % HOUR_MS) / MINUTE_MS);
    return minutes > 0 ? `${hours} h ${minutes} min` : `${hours} h`;
  }
  const days = Math.floor(ms / DAY_MS);
  const hours = Math.floor((ms % DAY_MS) / HOUR_MS);
  return hours > 0 ? `${days} d ${hours} h` : `${days} d`;
}

const ONE_DECIMAL = new Intl.NumberFormat("es-CO", { maximumFractionDigits: 1 });

/** "≈ 97 correos/hora" — integer from 10/h up, one decimal below that. */
export function formatSendRate(perHour: number): string {
  const rounded = perHour >= 10 ? Math.round(perHour) : Math.round(perHour * 10) / 10;
  const text = rounded >= 10 ? formatCount(rounded) : ONE_DECIMAL.format(rounded);
  return `≈ ${text} ${rounded === 1 ? "correo" : "correos"}/hora`;
}

/**
 * "Completada el 3 nov 2026, 13:33" once the send reached a final state and the API reported the
 * moment (`completadaEn`); "Finalizada el ..." for parcial/fallida. `null` (show nothing) while
 * sending, for cancelled campaigns, and for older campaigns without the timestamp.
 */
export function completionLine(estado: string, completadaEn: string | null | undefined): string | null {
  if (estado !== "completada" && estado !== "parcial" && estado !== "fallida") return null;
  if (!completadaEn || Number.isNaN(Date.parse(completadaEn))) return null;
  return `${estado === "completada" ? "Completada" : "Finalizada"} el ${formatDateTime(completadaEn)}`;
}

export interface PaceCard {
  value: string;
  hint: string;
}

const RATE_HINT = "Promedio de correos enviados por hora";

export function sendPaceCards(input: SendPaceInput): { time: PaceCard; rate: PaceCard } {
  const pace = computeSendPace(input);

  const time: PaceCard =
    pace.elapsedMs === null
      ? { value: "—", hint: "Aún no hay correos enviados" }
      : {
          value: formatSendDuration(pace.elapsedMs),
          hint: pace.inProgress ? "En curso…" : "Del primer al último correo enviado",
        };

  if (pace.perHour !== null) return { time, rate: { value: formatSendRate(pace.perHour), hint: RATE_HINT } };
  return { time, rate: { value: "—", hint: "Se calcula cuando hay más de un envío" } };
}
